import {
  initialize as initializeLDClient,
  LDClient,
  LDSingleKindContext,
} from 'launchdarkly-js-client-sdk';
import { v4 as getUUID } from 'uuid';
import { ENVS, Env } from '@adsk/acs-sc-web-platform-api';
import { DEFAULT_FEATURE_FLAGS_VALUES } from '../constants/featureFlags';

export const ENV_CONFIGS = {
  [ENVS.QA]: {
    LAUNCH_DARKLY: {
      API_KEY: '5ae05e8216d8f82f3a32ed19',
      USE_DEFAULTS: false,
    },
  },
  [ENVS.STAGING]: {
    LAUNCH_DARKLY: {
      API_KEY: '5ae05e2d60898c2e9ee26a37',
      USE_DEFAULTS: false,
    },
  },
  [ENVS.PRODUCTION]: {
    LAUNCH_DARKLY: {
      API_KEY: '5ae05e2d60898c2e9ee26a38',
      USE_DEFAULTS: false,
    },
  },
};

const anonymousUser = { key: getUUID(), anonymous: true };
export type FeatureFlagsValues = Record<string, unknown>;
class FeatureFlags {
  private client?: LDClient;

  private defaultFeatureFlagsValues: FeatureFlagsValues;

  constructor() {
    this.defaultFeatureFlagsValues = DEFAULT_FEATURE_FLAGS_VALUES;
  }

  initialize(env: Env, user: LDSingleKindContext, defaultFeatureFlagsValues?: FeatureFlagsValues) {
    if (defaultFeatureFlagsValues) {
      this.defaultFeatureFlagsValues = defaultFeatureFlagsValues;
    }

    return new Promise((resolve) => {
      // Reset initialized flag on each call to this function (which happens on each mount of acc-rfis)
      let wasClientInitialized = false;

      // Initialize LD client
      // We use a random user id as we currently don't have any FFs per user and should
      // decide how we would like to identify our users when we do
      const envKey = ENV_CONFIGS[env].LAUNCH_DARKLY.API_KEY;
      const ldUser = { ...anonymousUser, ...user };
      const options = {
        bootstrap: ENV_CONFIGS[env].LAUNCH_DARKLY.USE_DEFAULTS
          ? this.defaultFeatureFlagsValues
          : undefined,
        allAttributesPrivate: true,
      };
      this.client = initializeLDClient(envKey, ldUser, options);
      this.client.on('ready', () => {
        // If the LD client didn't initialize in time it means that we're now using default values.
        // So it's much safer to continue using them instead of switching between the two because they may be different.
        if (!wasClientInitialized) {
          wasClientInitialized = true;
          resolve(true);
        }
      });

      // if client didn't initialize in time, carry on on return defaults
      setTimeout(() => {
        if (!wasClientInitialized) {
          wasClientInitialized = true;
          resolve(true);
        }
      }, 5000);
    });
  }

  get(key: string) {
    const defaultFlagValue = this.defaultFeatureFlagsValues[key];
    const value = this.client ? this.client.variation(key, defaultFlagValue) : defaultFlagValue;
    // The convention for multi-variant values is that the "empty" value should be 'null'.
    // The rest of the values return as is.
    return value === 'null' ? null : value;
  }
}

const instance = new FeatureFlags();

export default instance;
